import React, { useEffect } from 'react';
import Logo from '@app/components/ui/logo';
import { useRouter } from 'next/router';
import { useAppSelector } from '@app/store/hooks';
import { selectAuth } from '@app/store/auth/authSlice';
import { useLazyGetAuthUrlQuery } from '@app/store/login/api';
import cn from 'classnames';
import Button from '@app/components/ui/button';
import { useTranslation } from 'next-i18next';
import ProfileDropdown from '@app/components/ui/profile-dropdown';
import LocaleDropdownUi from '@app/components/ui/locale-dropdown-ui';
import { statusErrorMessageHandler } from '@app/utils/errorUtils';
import HomeIcon from '@app/components/icons/home';
import Link from 'next/link';
import { selectActiveOrganization } from '@app/store/active/slice';
import { apm } from '@elastic/apm-rum';
import { ComponentNames, SpanType } from '@app/constants/elastic-apm';
import environments from '@app/configs/environments';

function Navbar({ className }: { className?: string }) {
    const router = useRouter();
    const auth = useAppSelector(selectAuth);
    const { t } = useTranslation(['common']);
    const [getAuthUrl, data] = useLazyGetAuthUrlQuery();
    const size = router.pathname === '/' || router.pathname.includes('invitations') || router.pathname.includes('/pricing') || router.pathname.includes('/contact-us') ? 'large' : 'small';

    const activeOrganization = useAppSelector(selectActiveOrganization);

    if (data.isError) {
        // @ts-ignore
        statusErrorMessageHandler(data?.error.originalStatus || data?.error.status, t);
    }

    useEffect(() => {
        if (data.isSuccess) {
            router.push(data.data.authorizationUrl);
        }
         
    }, [data.isSuccess]);

    const handleOnClick = async () => {
        try {
            const span = apm.startSpan(ComponentNames.SignInButton, SpanType.USER_INTERACTION);
            const queryParams = {
                locale: router.locale ?? 'en',
                callbackUrl: environments.KEYCLOAK_CALLBACK_URL || `${window.location.origin}/oauth/keycloak/callback`
            };
            await getAuthUrl(queryParams);
            if (span) span.end();
        } catch (e: any) {
            console.error('error: ', e);
        }
    };

    return (
        <nav className={cn('!fixed top-0 !z-30 flex w-full items-center justify-between bg-white-200 ', size === 'small' ? 'h-[32px] !bg-blue-100 px-1' : 'h-[72px] px-5  lg:px-[60px]', className)}>
            {router.pathname === '/' ? <Logo size={size} /> : <></>}
            {(router.pathname.includes('/pricing') || router.pathname.includes('/contact-us')) && (
                <Link href={`/${activeOrganization?.id}/dashboard`}>
                    <div className="flex cursor-pointer items-center gap-2">
                        <HomeIcon />
                        <h1 className={'text-sm font-semibold text-[#767F91]'}>Dashboard</h1>
                    </div>
                </Link>
            )}
            <div className="flex items-center gap-2 md:gap-10">
                {(router.pathname === '/' || router.pathname.includes('invitation') || router.pathname.includes('/pricing') || router.pathname.includes('/contact-us')) && <LocaleDropdownUi />}
                {router.pathname === '/' && (
                    <Button data-testid="signin-button" className="!rounded-[40px] !text-brand" variant={'outline'} shape={'rounded'} onClick={handleOnClick}>
                        {t('signIn')}
                    </Button>
                )}
                {((auth.email !== '' && (router.pathname.includes('invitation') || router.pathname.includes('/pricing'))) || router.pathname.includes('/contact-us')) && <ProfileDropdown isRoleVisible={false} />}
            </div>
        </nav>
    );
}

export default Navbar;
